import React, { useCallback, useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { apiGet, apiPatch, formatEuro } from "../../lib/api";
import {
  PIZZERIA_ORDER_LAUNCH_STATUS_LABELS,
  PIZZERIA_ORDER_STATUS_LABELS,
  PIZZERIA_ORDER_STATUS_OPTIONS,
  buildAdminAuthHeaders,
  formatDateTime,
  isUnauthorizedAdminError,
  shortOrderId
} from "./admin-utils";
import "../Shop.css";

const STATUS_FILTER_OPTIONS = [
  { value: "", label: "Tutti gli stati" },
  ...PIZZERIA_ORDER_STATUS_OPTIONS.map((status) => ({
    value: status,
    label: PIZZERIA_ORDER_STATUS_LABELS[status]
  }))
];

const DATE_FILTER_OPTIONS = [
  { value: "", label: "Tutte le date" },
  { value: "today", label: "Oggi" },
  { value: "7d", label: "Ultimi 7 giorni" },
  { value: "30d", label: "Ultimi 30 giorni" }
];

function formatItemLine(item) {
  const variant = item.variant_label ? ` (${item.variant_label})` : "";
  return `${item.name_snapshot}${variant} x${item.qty}`;
}

function resolveDateFilterRange(dateFilter) {
  if (!dateFilter) {
    return {};
  }

  const now = new Date();
  const from = new Date(now);

  if (dateFilter === "today") {
    from.setHours(0, 0, 0, 0);
  } else if (dateFilter === "7d") {
    from.setDate(from.getDate() - 7);
  } else if (dateFilter === "30d") {
    from.setDate(from.getDate() - 30);
  } else {
    return {};
  }

  return {
    from: from.toISOString(),
    to: now.toISOString()
  };
}

const AdminPizzeriaOrdersPage = () => {
  const { adminToken, handleUnauthorized } = useOutletContext();
  const [orders, setOrders] = useState([]);
  const [statusDraftById, setStatusDraftById] = useState({});
  const [statusFilter, setStatusFilter] = useState("");
  const [dateFilter, setDateFilter] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [savingOrderId, setSavingOrderId] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const loadOrders = useCallback(
    async ({ refreshOnly = false } = {}) => {
      if (!adminToken) {
        return;
      }

      setError("");
      setNotice("");
      if (refreshOnly) {
        setIsRefreshing(true);
      } else {
        setIsLoading(true);
      }

      try {
        const params = new URLSearchParams({ limit: "100" });
        if (statusFilter) {
          params.set("status", statusFilter);
        }
        const dateRange = resolveDateFilterRange(dateFilter);
        if (dateRange.from) {
          params.set("from", dateRange.from);
        }
        if (dateRange.to) {
          params.set("to", dateRange.to);
        }

        const payload = await apiGet(`/api/admin/pizzeria-orders?${params.toString()}`, {
          headers: buildAdminAuthHeaders(adminToken)
        });
        const nextOrders = Array.isArray(payload?.orders) ? payload.orders : [];
        setOrders(nextOrders);

        const nextDrafts = {};
        for (const order of nextOrders) {
          nextDrafts[order.id] = order.status;
        }
        setStatusDraftById(nextDrafts);
      } catch (loadError) {
        const message = loadError instanceof Error ? loadError.message : "Errore caricamento ordini pizzeria";
        if (isUnauthorizedAdminError(loadError)) {
          handleUnauthorized();
          return;
        }
        setError(message);
      } finally {
        if (refreshOnly) {
          setIsRefreshing(false);
        } else {
          setIsLoading(false);
        }
      }
    },
    [adminToken, dateFilter, handleUnauthorized, statusFilter]
  );

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  const handleStatusChange = (orderId, value) => {
    setStatusDraftById((current) => ({
      ...current,
      [orderId]: value
    }));
  };

  const handleStatusSave = async (orderId) => {
    const nextStatus = statusDraftById[orderId];
    if (!PIZZERIA_ORDER_STATUS_OPTIONS.includes(nextStatus)) {
      setError("Stato ordine pizzeria non valido.");
      return;
    }

    setSavingOrderId(orderId);
    setError("");
    setNotice("");

    try {
      const payload = await apiPatch(
        `/api/admin/pizzeria-orders/${encodeURIComponent(orderId)}`,
        { status: nextStatus },
        {
          headers: buildAdminAuthHeaders(adminToken)
        }
      );
      const updatedOrder = payload?.order;

      setOrders((currentOrders) =>
        currentOrders.map((order) =>
          order.id === orderId ? { ...order, status: updatedOrder?.status || nextStatus } : order
        )
      );
      setNotice("Stato ordine pizzeria aggiornato.");
    } catch (saveError) {
      const message = saveError instanceof Error ? saveError.message : "Errore aggiornamento stato";
      if (isUnauthorizedAdminError(saveError)) {
        handleUnauthorized();
        return;
      }
      setError(message);
    } finally {
      setSavingOrderId("");
    }
  };

  return (
    <section className="admin-section-block">
      <div className="admin-section-header">
        <div>
          <h3 className="admin-section-title">Ordini pizzeria WhatsApp</h3>
          <p className="admin-row-meta">
            Ordini generati dal sito prima dell&apos;apertura di WhatsApp. La conferma finale resta su WhatsApp.
          </p>
        </div>
        <div className="admin-header-actions">
          <select
            className="admin-status-select"
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
          >
            {STATUS_FILTER_OPTIONS.map((option) => (
              <option key={option.value || "all"} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <select
            className="admin-status-select"
            value={dateFilter}
            onChange={(event) => setDateFilter(event.target.value)}
          >
            {DATE_FILTER_OPTIONS.map((option) => (
              <option key={option.value || "all-dates"} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <button
            className="cart-clear-button"
            type="button"
            onClick={() => loadOrders({ refreshOnly: true })}
            disabled={isRefreshing || isLoading}
          >
            {isRefreshing ? "Aggiornamento..." : "Ricarica ordini"}
          </button>
        </div>
      </div>

      {error && <p className="shop-state shop-error">Errore: {error}</p>}
      {!error && notice && <p className="shop-state admin-success">{notice}</p>}
      {isLoading && <p className="shop-state">Caricamento ordini pizzeria...</p>}
      {!isLoading && orders.length === 0 && <p className="shop-state">Nessun ordine pizzeria disponibile.</p>}

      {!isLoading && orders.length > 0 && (
        <div className="admin-table-wrap">
          <table className="admin-pizzeria-orders-table">
            <thead>
              <tr>
                <th>Ordine</th>
                <th>Riepilogo</th>
                <th>Totale</th>
                <th>Stato</th>
                <th>WhatsApp</th>
                <th>Azione</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((order) => {
                const isSaving = savingOrderId === order.id;
                const draftStatus = statusDraftById[order.id] || order.status;
                const items = Array.isArray(order.items) ? order.items : [];

                return (
                  <tr key={order.id}>
                    <td>
                      <strong>{shortOrderId(order.id)}</strong>
                      <div className="admin-row-meta">{formatDateTime(order.created_at)}</div>
                      <div className="admin-row-meta">{order.source || "site_menu_whatsapp"}</div>
                    </td>
                    <td>
                      <ul className="admin-pizzeria-order-items">
                        {items.map((item, index) => (
                          <li key={`${order.id}-${item.menu_item_id}-${item.price_index}-${index}`}>
                            {formatItemLine(item)}
                          </li>
                        ))}
                      </ul>
                      <details className="admin-pizzeria-order-message">
                        <summary>Testo WhatsApp</summary>
                        <pre>{order.whatsapp_message}</pre>
                      </details>
                    </td>
                    <td>
                      <div className="admin-row-primary">{formatEuro(order.estimated_total_cents)}</div>
                      <div className="admin-row-meta">{order.total_items || 0} pezzi</div>
                    </td>
                    <td>
                      <select
                        className="admin-status-select"
                        value={draftStatus}
                        onChange={(event) => handleStatusChange(order.id, event.target.value)}
                      >
                        {PIZZERIA_ORDER_STATUS_OPTIONS.map((statusCode) => (
                          <option key={statusCode} value={statusCode}>
                            {PIZZERIA_ORDER_STATUS_LABELS[statusCode]}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      {order.whatsapp_url ? (
                        <a className="shop-back-link" href={order.whatsapp_url} target="_blank" rel="noreferrer">
                          Apri messaggio
                        </a>
                      ) : (
                        "-"
                      )}
                      <div className="admin-row-meta">
                        {PIZZERIA_ORDER_LAUNCH_STATUS_LABELS[order.launch_status] || "Creato dal sito"}
                      </div>
                      <div className="admin-row-meta">
                        {order.client_id ? `Client ${order.client_id.slice(0, 8)}` : "-"}
                      </div>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="shop-add-button admin-save-button"
                        onClick={() => handleStatusSave(order.id)}
                        disabled={isSaving}
                      >
                        {isSaving ? "Salvataggio..." : "Salva"}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};

export default AdminPizzeriaOrdersPage;
